import { useState, useRef, useCallback } from 'react';
import { inventoryService } from '@/services/inventoryService';
import { Wine, StagedWine, IngestionState } from '@/types';
import { CONFIG } from '@/constants';
import { enrichWine } from '@/services/enrichmentService';
import { sanitizeWineName } from '@/utils/wineNameGuard';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import { useProfile } from '@/context/ProfileContext';

export function useToolHandler(localCellar: Wine[]) {
  const { isPremium } = useProfile();
  const [stagedWine, setStagedWine] = useState<StagedWine | null>(null);
  const [ingestionState, setIngestionState] = useState<IngestionState>('IDLE');
  const stagedWineRef = useRef<StagedWine | null>(null);

  const handleToolCalls = useCallback(async (calls: any[]) => {
    const results: { result: string }[] = [];
    for (const call of calls) {
      if (call.name === 'queryInventory') {
        try {
          const res = await authFetch(FUNCTION_URLS.queryInventory, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(call.args),
          });
          if (!res.ok) throw new Error(`queryInventory error: ${res.status}`);
          const data = await res.json();
          const wines = data.wines || [];
          if (wines.length === 0) {
            results.push({ result: `No wines found matching those criteria. Total in cellar: ${data.total || 0}.` });
          } else {
            const formatted = wines.map((w: any) => {
              const parts = [
                `${w.producer}${w.name ? ' ' + w.name : ''} ${w.vintage || 'NV'}`,
                `${w.type || ''}, ${w.region || ''}${w.country ? ', ' + w.country : ''}`,
                `$${w.price || 0} — Qty: ${w.quantity || 1}`,
                `Maturity: ${w.maturity || 'Unknown'}`,
              ];
              if (w.grapeVarieties?.length) parts.push(`Grape: ${w.grapeVarieties.map((g: any) => g.name).join(', ')}`);
              if (w.tastingNotes) parts.push(`Notes: ${w.tastingNotes}`);
              if (w.criticRating) parts.push(`Rating: ${w.criticRating}/100`);
              if (w.drinkFrom || w.drinkUntil) parts.push(`Window: ${w.drinkFrom || '?'}–${w.drinkUntil || '?'}`);
              if (w.appellation) parts.push(`Appellation: ${w.appellation}`);
              return parts.join(' — ');
            }).join('\n');
            results.push({ result: `Found ${data.total} wines (showing ${wines.length}):\n${formatted}` });
          }
        } catch (err) {
          console.error('queryInventory call failed, using fallback:', err);
          const fallback = inventoryService.buildCellarSummary(localCellar);
          results.push({ result: `queryInventory is temporarily unavailable. Here is a summary of the cellar instead: ${fallback}` });
        }
      } else if (call.name === 'stageWine') {
        const args = sanitizeWineName(call.args);
        const staged = { ...args, stagedId: Date.now().toString() };
        stagedWineRef.current = staged;
        setStagedWine(staged);
        setIngestionState('STAGED');
        results.push({ result: "Wine staged. Now ask the user for price and quantity." });
      } else if (call.name === 'commitWine') {
        const currentStaged = stagedWineRef.current;
        if (!currentStaged) {
          results.push({ result: "Error: No wine staged." });
        } else {
          // Defensive bottle cap check (Rémy is premium-only, but guard anyway)
          const totalBottles = localCellar.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);
          if (!isPremium && totalBottles + (call.args.quantity || 1) > CONFIG.FREE_TIER.MAX_BOTTLES) {
            results.push({ result: "Cannot add wine — cellar is full on the free plan. The user needs to upgrade to Premium." });
          } else {
            const finalWine = {
              ...currentStaged,
              price: call.args.price,
              quantity: call.args.quantity || 1,
              name: currentStaged.name || ''
            };
            const id = await inventoryService.addWine(finalWine as any);
            if (id) {
              enrichWine(id, finalWine as Partial<Wine>).catch(err =>
                console.error('Post-commit enrichment failed (non-blocking):', err));
            }
            setIngestionState('COMMITTED');
            stagedWineRef.current = null;
            setStagedWine(null);
            results.push({ result: `Success! Wine added to cellar with ID ${id}.` });
          }
        }
      }
    }
    return results;
  }, [localCellar, isPremium]);

  return { handleToolCalls, stagedWine, ingestionState, stagedWineRef };
}
